import React, { useState, useRef, useEffect } from 'react';
import { ChatMessage, Medium, UserLocation, GuestUser, ClientMemory } from '../types';
import { getChatResponse, analyzeConversation, cleanAiText, splitIntoSentences, calculateTypingDelay } from '../services/geminiService';
import { SendIcon, XIcon, EyeIcon, MinusIcon, PhoneIcon, StarIcon, ShieldIcon, LockIcon, RobotIcon, VolumeIcon, VolumeXIcon, CheckIcon, CheckCheckIcon, SparklesIcon, CardsIcon, CameraIcon } from './Icons';
import { useAdmin } from '../contexts/AdminContext';
import { useClient } from '../contexts/ClientContext'; 
import { useChatLayout } from '../contexts/ChatLayoutContext';
import { socket, getApiUrl, formatImageUrl, compressImage } from '../services/socket';
import { MAJOR_ARCANA, TAROT_IMAGES } from './TarotOracle';
import { aiQueue } from '../services/aiQueue';

interface LiveChatProps {
    currentMedium: Medium;
    guestUser: GuestUser | null;
    userLocation?: UserLocation; 
    onClose: () => void;
    embedded?: boolean;
}

type ChatStatus = 'idle' | 'countdown' | 'reading' | 'typing';

const LiveChat: React.FC<LiveChatProps> = ({ currentMedium, guestUser, userLocation, onClose, embedded = false }) => {
    const { 
        createSession, 
        addMessageToSession, 
        getSession, 
        sessions,
        sessionsHistory,
        isAiEnabled, 
        isChatAutoAnswerEnabled,
        toggleChatAutoAnswer,
        isAuthenticated,
        customApiKey,
        logSubmission,
        getAiMemory,    
        updateAiMemory,
        isMaintenanceMode,
        globalMediumStatus
    } = useAdmin();

    const { client, addHistoryEntry, addReview, openAuthModal, recordSessionDurationAndPoints, addLoyaltyPoints } = useClient(); 
    const pointsAwardedRef = useRef(false);
    const [earnedSessionPoints, setEarnedSessionPoints] = useState<number>(0);
    const [floatingAstresBadge, setFloatingAstresBadge] = useState<string | null>(null); 

    const activeUser = client ? { 
        name: client.name, 
        phone: client.phone, 
        address: client.address, 
        gender: client.gender,
        birthDate: client.birthDate
    } : (guestUser || { 
        name: 'Consultant(e)', 
        phone: '0600000000', 
        address: 'France', 
        gender: 'femme' as const 
    });

    const [sessionId, setSessionId] = useState<string | null>(null);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [clientMemory, setClientMemory] = useState<ClientMemory | null>(null);

    // --- RATING STATE ---
    const [showRating, setShowRating] = useState(false);
    const [userRating, setUserRating] = useState(0);
    const [reviewText, setReviewText] = useState('');

    // --- PHONE UPSELL STATE ---
    const [showPhoneInvite, setShowPhoneInvite] = useState(false);
    const [hasShownPhoneInvite, setHasShownPhoneInvite] = useState(false);

    const [inputValue, setInputValue] = useState('');
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [status, setStatus] = useState<ChatStatus>('idle');
    const [countdown, setCountdown] = useState<number | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const [startTime] = useState(Date.now());
    
    const { getChatState, setMinimized, incrementUnread } = useChatLayout();
    const chatWindowState = getChatState(currentMedium.id);
    const isMinimized = chatWindowState?.isMinimized ?? false;
    const unreadCount = chatWindowState?.unreadCount ?? 0;
    
    const [isTerminated, setIsTerminated] = useState(false);
    const [showNewMessageBadge, setShowNewMessageBadge] = useState(false);
    const isAtBottomRef = useRef(true);
    const messageContainerRef = useRef<HTMLDivElement>(null);
    const [soundEnabled, setSoundEnabled] = useState<boolean>(() => {
        try {
            const saved = localStorage.getItem('astro_chat_sound_enabled');
            return saved !== null ? JSON.parse(saved) : true;
        } catch {
            return true;
        }
    });

    const toggleSound = () => {
        setSoundEnabled(prev => {
            const next = !prev;
            try {
                localStorage.setItem('astro_chat_sound_enabled', JSON.stringify(next));
            } catch (e) {
                console.error(e);
            }
            return next;
        });
    };
    
    const prevMessagesLengthRef = useRef(0);
    
    // Timer pour le délai de réponse IA (Debounce)
    const aiResponseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // isAiRespondingRef removed — replaced by global aiQueue singleton
    const lastHandledUserMsgIdRef = useRef<string | null>(null);
    const [tarotDrawnCards, setTarotDrawnCards] = useState<string[]>([]);

    // Timer de 10 minutes d'inactivité client
    const inactivityTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // --- STARS BACKGROUND STATE ---
    const [stars, setStars] = useState<{id: number, left: number, top: number, size: number, duration: number, delay: number}[]>([]);

    useEffect(() => {
        // Génération des étoiles d'ambiance
        setStars(Array.from({ length: 40 }).map((_, i) => ({
            id: i,
            left: Math.random() * 100,
            top: Math.random() * 100,
            size: Math.random() * 2 + 1,
            duration: Math.random() * 3 + 2, // Entre 2 et 5s
            delay: Math.random() * 5
        })));
    }, []);

    // Vérification de suspension / bannissement du client en temps réel
    useEffect(() => {
        if (client && client.isSuspended) {
            setIsTerminated(true);
        } else if (activeUser) {
            try {
                const clients = JSON.parse(localStorage.getItem('astro_clients_db') || '[]');
                const found = clients.find((c: any) => (client && c.id === client.id) || (c.phone && activeUser.phone && c.phone === activeUser.phone) || (c.name && activeUser.name && (c.name || '').toLowerCase() === (activeUser.name || '').toLowerCase()));
                if (found && found.isSuspended) {
                    setIsTerminated(true);
                }
            } catch (e) {}
        }

        const handleSuspended = () => {
            setIsTerminated(true);
        };
        window.addEventListener('astro_client_suspended', handleSuspended);
        return () => {
            window.removeEventListener('astro_client_suspended', handleSuspended);
        };
    }, [client, activeUser]);

    // Initialisation et Changement de Session (ISOLATION STRICTE PAR MÉDIUM ET PAR CLIENT)
    useEffect(() => {
        if (!activeUser || !currentMedium) return;

        const loc = userLocation || (client?.location);
        const clientPrefix = client?.id ? client.id : (activeUser?.phone ? activeUser.phone.replace(/\s+/g, '') : 'guest');
        const storageKey = `astro_chat_${clientPrefix}_m${currentMedium.id}`;
        
        // 1. Tenter de récupérer l'historique local propre à CE client et CE médium
        const savedData = localStorage.getItem(storageKey);
        let restoredId = null;
        let restoredMessages: ChatMessage[] = [];
        let restoredTerminated = false;

        if (savedData) {
            try {
                const parsed = JSON.parse(savedData);
                if (parsed && parsed.sessionId) {
                    restoredId = parsed.sessionId;
                    restoredMessages = parsed.messages || [];
                    if (parsed.isTerminated) restoredTerminated = true;
                }
            } catch (e) {
                console.error("Erreur lecture sauvegarde chat", e);
            }
        }

        setMessages(restoredMessages);
        setIsTerminated(restoredTerminated);
        setInputValue('');

        // 2. Créer ou Restaurer la session isolée strictement par clientId (ID canonique)
        const clientOwnerId = client?.id || (activeUser?.phone ? `guest_${activeUser.phone.replace(/\s+/g, '')}` : undefined);
        const canonicalSessionId = currentMedium.id === 9999 ? `sarah_${clientOwnerId || 'guest'}` : `session_${clientOwnerId || 'guest'}_m${currentMedium.id}`;
        const newId = createSession(
            currentMedium, 
            activeUser.name, 
            activeUser.phone, 
            activeUser.address, 
            loc,
            canonicalSessionId, 
            restoredMessages,
            clientOwnerId,
            client?.email || (activeUser as any)?.email
        );
        
        setSessionId(newId);

        // --- CHARGEMENT DE LA MÉMOIRE ISOLÉE DU MÉDIUM ---
        if (activeUser.phone) {
            const mem = getAiMemory(activeUser.phone, currentMedium.id);
            if (mem) {
                setClientMemory(mem);
                if (mem.isBlocked) {
                    setIsTerminated(true);
                }
            } else {
                setClientMemory(null);
            }
        }
    }, [currentMedium.id, client?.id, activeUser?.phone]);

    // --- SYNCHRONISATION AUTOMATIQUE AVEC L'HISTORIQUE CLIENT ---
    useEffect(() => {
        if (client && sessionId && currentMedium) {
            addHistoryEntry({
                id: sessionId,
                mediumName: currentMedium.name,
                mediumImage: currentMedium.imageUrl,
                date: new Date().toLocaleDateString('fr-FR', { 
                    day: 'numeric', month: 'long', year: 'numeric',
                    hour: '2-digit', minute: '2-digit' 
                }),
                duration: "En cours",
                notes: "Consultation Chat"
            });
        }
    }, [client, sessionId, currentMedium, addHistoryEntry]);

    // SYNCHRONISATION DIRECTE DEPUIS LE CONTEXTE ADMIN / SESSIONS
    const activeContextSession = sessions.find(s => s.id === sessionId) || 
                                 sessionsHistory.find(s => s.id === sessionId) ||
                                 sessions.find(s => String(s.mediumId) === String(currentMedium.id) && (
                                     (client?.id && s.clientId === client.id) ||
                                     (s.id && s.id.includes(`_m${currentMedium.id}`))
                                 ));

    useEffect(() => {
        if (activeContextSession && Array.isArray(activeContextSession.messages) && activeContextSession.messages.length > messages.length) {
            setMessages(activeContextSession.messages);
            setStatus('idle');
        }
    }, [activeContextSession, messages.length]);

    // Socket Listener pour messages entrants & mises à jour de session en temps réel
    useEffect(() => {
        if (!sessionId) return;

        socket.emit('join_session', sessionId);

        const handleIncomingMessage = (data: any) => {
            if (!data) return;
            const incomingMsg: ChatMessage = data.message || data;
            const msgSessionId: string = data.sessionId || incomingMsg?.sessionId;
            const msgMediumId = data.mediumId || incomingMsg?.mediumId;

            // Vérifier si le message concerne cette session ou ce médium
            const isMatch = (msgSessionId && msgSessionId === sessionId) || 
                            (msgMediumId && String(msgMediumId) === String(currentMedium.id));

            if (!isMatch && msgSessionId) return;

            if (incomingMsg && incomingMsg.id) {
                setStatus('idle');
                if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);

                setMessages(prev => {
                    if (prev.some(m => m.id === incomingMsg.id)) return prev;
                    return [...prev, incomingMsg];
                });
            }
        };

        const handleSessionUpdated = (updatedSession: any) => {
            if (!updatedSession) return;
            const isSameSession = updatedSession.id === sessionId;
            const isSameMedium = String(updatedSession.mediumId) === String(currentMedium.id);

            if (isSameSession || isSameMedium) {
                if (Array.isArray(updatedSession.messages) && updatedSession.messages.length > 0) {
                    setMessages(updatedSession.messages);
                    setStatus('idle');
                    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
                }
            }
        };

        socket.on('new_message', handleIncomingMessage);
        socket.on('receive_message', handleIncomingMessage);
        socket.on('session_updated', handleSessionUpdated);

        const handleTypingEvent = (data: any) => {
            if (!data) return;
            if (typeof data === 'boolean') {
                setStatus(data ? 'typing' : 'idle');
                if (data) {
                    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
                    typingTimeoutRef.current = setTimeout(() => setStatus('idle'), 15000);
                } else if (typingTimeoutRef.current) {
                    clearTimeout(typingTimeoutRef.current);
                }
                return;
            }
            if (data.sessionId && data.sessionId !== sessionId) return;
            setStatus(data.isTyping ? 'typing' : 'idle');
            if (data.isTyping) {
                if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
                typingTimeoutRef.current = setTimeout(() => setStatus('idle'), 15000);
            } else if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
            }
        };

        socket.on('typing', handleTypingEvent);
        socket.on('typing_status', handleTypingEvent);
        socket.on('session_typing', handleTypingEvent);

        return () => {
            socket.off('new_message', handleIncomingMessage);
            socket.off('receive_message', handleIncomingMessage);
            socket.off('session_updated', handleSessionUpdated);
            socket.off('typing', handleTypingEvent);
            socket.off('typing_status', handleTypingEvent);
            socket.off('session_typing', handleTypingEvent);
            if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
            if (sessionId) aiQueue.cancel(sessionId);
        };
    }, [sessionId, currentMedium.id]);

    // SAUVEGARDE AUTOMATIQUE LOCALSTORAGE (ISOLÉE PAR CLIENT & MÉDIUM)
    useEffect(() => {
        if (sessionId && messages.length > 0) {
            const clientPrefix = client?.id ? client.id : (activeUser?.phone ? activeUser.phone.replace(/\s+/g, '') : 'guest');
            const storageKey = `astro_chat_${clientPrefix}_m${currentMedium.id}`;
            localStorage.setItem(storageKey, JSON.stringify({
                sessionId: sessionId,
                messages: messages,
                isTerminated: isTerminated
            }));
        }
    }, [sessionId, messages, isTerminated, currentMedium.id, client, activeUser]);

    // Notification Logic
    useEffect(() => {
        if (messages.length > prevMessagesLengthRef.current) {
            const lastMsg = messages[messages.length - 1];
            if (lastMsg.role !== 'user') {
                if (isMinimized) {
                    incrementUnread(currentMedium.id);
                }
                playNotificationSound();
            }
        }
        prevMessagesLengthRef.current = messages.length;
    }, [messages, isMinimized, soundEnabled, currentMedium.id, incrementUnread]);

    // --- UPSELL PHONE TRIGGER (8 messages) ---
    useEffect(() => {
        const userMsgCount = messages.filter(m => m.role === 'user').length;
        if (userMsgCount >= 8 && !hasShownPhoneInvite) {
            setTimeout(() => {
                setShowPhoneInvite(true);
                setHasShownPhoneInvite(true);
            }, 2000);
        }
    }, [messages, hasShownPhoneInvite]);

    const scrollToBottom = () => {
        setShowNewMessageBadge(false);
        isAtBottomRef.current = true;
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    // Gestion intelligente du scroll : Ne pas forcer le scroll vers le bas si le client est en train de lire
    useEffect(() => {
        const currentLength = messages.length;
        if (currentLength > prevMessagesLengthRef.current) {
            prevMessagesLengthRef.current = currentLength;
            const lastMsg = messages[messages.length - 1];
            // Si c'est l'utilisateur qui a écrit OU s'il est déjà en bas de page
            if (lastMsg?.role === 'user' || isAtBottomRef.current) {
                scrollToBottom();
            } else {
                // Le client a scrollé vers le haut : afficher le badge Nouveau Message
                setShowNewMessageBadge(true);
            }
        } else if (isAtBottomRef.current) {
            scrollToBottom();
        }
    }, [messages, status, countdown]);

    const handleMessagesScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        const isNearBottom = scrollHeight - scrollTop - clientHeight < 70;
        isAtBottomRef.current = isNearBottom;
        if (isNearBottom) {
            setShowNewMessageBadge(false);
        }
    };

    const playNotificationSound = () => {
        if (!soundEnabled) return;
        try {
            const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
            if (!AudioCtx) return;
            const audioCtx = new AudioCtx();

            if (audioCtx.state === 'suspended') {
                audioCtx.resume();
            }

            const now = audioCtx.currentTime;

            // Subtle celestial two-note chime (Note 1: C5 = 523.25Hz -> Note 2: G5 = 783.99Hz)
            const osc1 = audioCtx.createOscillator();
            const gain1 = audioCtx.createGain();
            osc1.type = 'sine';
            osc1.frequency.setValueAtTime(523.25, now);
            gain1.gain.setValueAtTime(0.001, now);
            gain1.gain.linearRampToValueAtTime(0.035, now + 0.02);
            gain1.gain.exponentialRampToValueAtTime(0.0001, now + 0.25);
            osc1.connect(gain1);
            gain1.connect(audioCtx.destination);
            osc1.start(now);
            osc1.stop(now + 0.25);

            const osc2 = audioCtx.createOscillator();
            const gain2 = audioCtx.createGain();
            osc2.type = 'sine';
            osc2.frequency.setValueAtTime(783.99, now + 0.07);
            gain2.gain.setValueAtTime(0.001, now + 0.07);
            gain2.gain.linearRampToValueAtTime(0.045, now + 0.09);
            gain2.gain.exponentialRampToValueAtTime(0.0001, now + 0.35);
            osc2.connect(gain2);
            gain2.connect(audioCtx.destination);
            osc2.start(now + 0.07);
            osc2.stop(now + 0.35);
        } catch (e) {
            // Silent fail
        }
    };

    const simulateHumanTyping = async (fullText: string, currentSessionId: string, cardImageUrl?: string) => {
        if (!fullText || !fullText.trim()) return;

        // 2. Nettoyage du texte (Clean Text) : suppression absolue de tout formatage Markdown (*, #, `, _, ~)
        const cleaned = cleanAiText(fullText);
        if (!cleaned) return;

        // 3. Découpage intelligent (Splitting) : tableau de phrases avec ponctuation conservée
        const sentences = splitIntoSentences(cleaned);
        if (sentences.length === 0) return;

        // 5. Logique d'émission asynchrone (for ... of) avec simulation de frappe humaine
        for (let i = 0; i < sentences.length; i++) {
            const sentence = sentences[i];
            if (!sentence || !sentence.trim()) continue;

            // a. Émettre l'indicateur "typing: true" au client via Socket.io et état local
            setStatus('typing');
            socket.emit('typing_status', { 
                sessionId: currentSessionId, 
                isTyping: true, 
                role: 'model', 
                mediumId: currentMedium.id 
            });

            // b. Attendre le temps calculé par la fonction "Typing Delay" (80ms/caractère + 1500ms, max 12s)
            const delayMs = calculateTypingDelay(sentence);
            await new Promise(resolve => setTimeout(resolve, delayMs));

            // c. Émettre l'indicateur "typing: false"
            setStatus('idle');
            socket.emit('typing_status', { 
                sessionId: currentSessionId, 
                isTyping: false, 
                role: 'model', 
                mediumId: currentMedium.id 
            });

            // d. Émettre la phrase au client
            const aiMsg: ChatMessage = {
                id: (Date.now() + i).toString(),
                role: 'model',
                text: sentence,
                timestamp: new Date(),
                imageUrl: (i === 0 && cardImageUrl) ? cardImageUrl : undefined
            };
            addMessageToSession(currentSessionId, aiMsg);

            // e. Ajouter une petite pause naturelle (800ms) avant de traiter la phrase suivante
            if (i < sentences.length - 1) {
                await new Promise(resolve => setTimeout(resolve, 800));
            }
        }
    };

    const triggerAiResponse = async (currentHistory: ChatMessage[]) => {
        const activeSessionId = sessionId || (currentMedium ? `session_${activeUser?.phone ? activeUser.phone.replace(/\s+/g, '') : 'guest'}_m${currentMedium.id}` : null);
        if (!activeSessionId) return;
        // isAiRespondingRef supprimé — la file d'attente globale aiQueue gère la concurrence
        setStatus('typing');

        try {
            const lastUserMsg = currentHistory.filter(m => m.role === 'user').pop()?.text || '';
            const lowerLastMsg = (lastUserMsg || '').toLowerCase();

            // TIRAGE DE TAROT INTERACTIF EN 3 CARTES AVEC IMAGES
            const isTarotRequested = /tirage|carte|cartes|tirer|tarot/i.test(lowerLastMsg);
            const isTarotContinuation = tarotDrawnCards.length > 0 && /oui|d'accord|ok|continue|volontiers|suivante|prochaine|2|3|deux|trois/i.test(lowerLastMsg);
            const isTarotStop = tarotDrawnCards.length > 0 && /non|arrête|stop|pas la peine|merci/i.test(lowerLastMsg);

            let cardToDraw: string | null = null;
            let cardImageUrl: string | undefined = undefined;
            let promptOverride: string | undefined = undefined;

            if (isTarotStop) {
                setTarotDrawnCards([]);
            } else if (isTarotRequested && tarotDrawnCards.length === 0) {
                // ÉTAPE 1 : TIRAGE DE LA 1ÈRE CARTE
                const available = MAJOR_ARCANA.filter(c => !tarotDrawnCards.includes(c));
                cardToDraw = available[Math.floor(Math.random() * available.length)];
                cardImageUrl = TAROT_IMAGES[cardToDraw];
                setTarotDrawnCards([cardToDraw]);

                promptOverride = `Le client demande un tirage de cartes. Tu viens de lui tirer la 1ère carte : "${cardToDraw}". Donne l'interprétation mystique et courte (2 phrases) de cette 1ère carte, puis demande-lui s'il souhaite que tu tires la 2ème carte.`;
            } else if (isTarotContinuation && tarotDrawnCards.length === 1) {
                // ÉTAPE 2 : TIRAGE DE LA 2ÈME CARTE
                const available = MAJOR_ARCANA.filter(c => !tarotDrawnCards.includes(c));
                cardToDraw = available[Math.floor(Math.random() * available.length)];
                cardImageUrl = TAROT_IMAGES[cardToDraw];
                setTarotDrawnCards([...tarotDrawnCards, cardToDraw]);

                promptOverride = `La 1ère carte était "${tarotDrawnCards[0]}". Tu tires maintenant la 2ème carte : "${cardToDraw}". Donne l'interprétation courte (2 phrases) de cette 2ème carte, puis demande-lui s'il souhaite découvrir la 3ème et dernière carte pour conclure le tirage.`;
            } else if (isTarotContinuation && tarotDrawnCards.length === 2) {
                // ÉTAPE 3 : TIRAGE DE LA 3ÈME CARTE ET SYNTHÈSE FINALE
                const available = MAJOR_ARCANA.filter(c => !tarotDrawnCards.includes(c));
                cardToDraw = available[Math.floor(Math.random() * available.length)];
                cardImageUrl = TAROT_IMAGES[cardToDraw];
                const card1 = tarotDrawnCards[0];
                const card2 = tarotDrawnCards[1];
                setTarotDrawnCards([]); // Reset après la 3ème carte

                promptOverride = `Tirage complet des 3 cartes : 1ère "${card1}", 2ème "${card2}", 3ème carte : "${cardToDraw}". Donne l'interprétation de cette 3ème carte et conclus la synthèse globale du tirage en 2 phrases positives.`;
            }

            // Call Ollama via server proxy
            let responseText = await getChatResponse(
                currentHistory,
                currentMedium, 
                activeUser?.name, 
                customApiKey,
                activeUser?.gender,
                clientMemory,
                activeUser?.birthDate,
                promptOverride
            );

            if (responseText.includes('[TERMINATE_SESSION]')) {
                responseText = responseText.replace('[TERMINATE_SESSION]', '').trim();
            }

            // Envoyer la réponse phrase par phrase avec l'image de la carte si disponible
            if (responseText && responseText.trim()) {
                await simulateHumanTyping(responseText, activeSessionId, cardImageUrl);
            }
        } catch (e) {
            console.error("AI Response error:", e);
        } finally {
            setStatus('idle');
            // La file d'attente aiQueue appelle processNext() automatiquement — pas besoin de retry manuel.
        }
    };

    const handleToggleBan = (e: React.MouseEvent) => {
        e.stopPropagation();
        const nextBlocked = !isTerminated;
        setIsTerminated(nextBlocked);
        if (activeUser?.phone && currentMedium.id) {
            updateAiMemory(
                activeUser.phone,
                currentMedium.id,
                activeUser.name || "Inconnu",
                clientMemory?.facts || [],
                clientMemory?.predictions || [],
                nextBlocked ? "Banni" : (clientMemory?.emotionalProfile || "Actif"),
                nextBlocked
            );
        }
    };

    const handleSend = () => {
        if (!inputValue.trim() || !sessionId || isTerminated) return;

        const userMsg: ChatMessage = {
            id: Date.now().toString(),
            sessionId: sessionId,
            mediumId: currentMedium.id,
            mediumName: currentMedium.name,
            role: 'user',
            text: inputValue,
            timestamp: new Date()
        };

        addMessageToSession(sessionId, userMsg);
        setInputValue('');
    };

    // --- SIMULATION DEV MODE ---
    const handleSimulateChat = () => {
        if (!sessionId) return;
        for (let i = 0; i < 4; i++) {
            const userMsg: ChatMessage = { id: `sim-u-${i}`, role: 'user', text: `Message simule ${i+1}`, timestamp: new Date() };
            addMessageToSession(sessionId, userMsg);
            const aiMsg: ChatMessage = { id: `sim-m-${i}`, role: 'model', text: `Réponse simulée ${i+1}`, timestamp: new Date() };
            addMessageToSession(sessionId, aiMsg);
        }
    };

    // --- INACTIVITÉ 10 MINUTES : MISE A JOUR CERVEAU IA SANS CLÔTURE DE LA DISCUSSION ---
    useEffect(() => {
        if (!sessionId || isTerminated) return;

        if (inactivityTimeoutRef.current) {
            clearTimeout(inactivityTimeoutRef.current);
        }

        const hasUserMessage = messages.some(m => m.role === 'user');
        if (hasUserMessage) {
            // 10 minutes = 600000ms
            inactivityTimeoutRef.current = setTimeout(async () => {
                console.log("10 min d'inactivité : Mise à jour automatique du cerveau IA...");
                
                await saveChatMemory();
                
                // La discussion n'est pas clôturée et reste pleinement accessible par le client
            }, 10 * 60 * 1000);
        }

        return () => {
            if (inactivityTimeoutRef.current) {
                clearTimeout(inactivityTimeoutRef.current);
            }
        };
    }, [messages, sessionId, isTerminated]);

    const saveChatMemory = async () => {
        const hasUserMessage = messages.some(m => m.role === 'user');
        if (hasUserMessage && activeUser?.phone && currentMedium.id) {
            console.log(`Analyse de conversation pour la mémoire de ${currentMedium.name}...`);
            try {
                const analysis = await analyzeConversation(messages);
                updateAiMemory(
                    activeUser.phone,
                    currentMedium.id,
                    activeUser.name || "Inconnu",
                    analysis.facts,
                    analysis.predictions,
                    analysis.emotionalState,
                    false, // isBlocked
                    analysis.summary,
                    analysis.concerns,
                    analysis.problem
                );
                console.log("Cerveau IA et Fiche Client mis à jour avec succès.");
            } catch (err) {
                console.error("Erreur lors de l'analyse mémoire:", err);
            }
        }
    };

    const awardPointsForCurrentSession = () => {
        if (pointsAwardedRef.current || !sessionId || !currentMedium) return 0;
        pointsAwardedRef.current = true;
        const durationMin = Math.max(0.5, (Date.now() - startTime) / (1000 * 60));
        const pts = recordSessionDurationAndPoints(sessionId, durationMin, currentMedium.name, currentMedium.imageUrl, currentMedium.id, messages);
        setEarnedSessionPoints(pts);
        return pts;
    };

    const handleManualEndChat = () => {
        saveChatMemory();
        awardPointsForCurrentSession();
        if (messages.length > 0) {
            setShowRating(true);
        } else {
            onClose();
        }
    };

    const handleCloseAttempt = () => {
        saveChatMemory();
        awardPointsForCurrentSession();
        if (messages.length > 0 && !showRating) {
            setShowRating(true);
        } else {
            onClose();
        }
    };

    const handleSubmitRating = () => {
        if (userRating > 0) {
            addReview(currentMedium.id, userRating, reviewText);
        }
        onClose();
    };

    const handleForceRegister = () => {
        if (!guestUser) return;
        openAuthModal({
            name: guestUser.name,
            email: guestUser.email,
            phone: guestUser.phone,
            address: guestUser.address,
            gender: guestUser.gender
        });
    };

    const userMessageCount = messages.filter(m => m.role === 'user').length;
    const isLimitReached = !client && userMessageCount >= 5;

    // Statut en temps réel de l'interlocuteur (Disponible, Occupé, Hors ligne)
    const getInterlocutorStatus = () => {
        if (globalMediumStatus === 'force_offline' || isTerminated) {
            return {
                type: 'offline' as const,
                label: 'Hors ligne',
                shortLabel: 'Hors ligne',
                subLabel: isTerminated ? 'Consultation terminée' : 'Absent(e) actuellement',
                badgeClass: 'bg-zinc-900/95 text-zinc-300 border-zinc-700/80 shadow-md',
                dotClass: 'bg-zinc-500',
                headerBg: 'bg-gradient-to-r from-zinc-900 via-gray-900 to-zinc-900',
                bannerBorder: 'border-zinc-700/60'
            };
        }

        if (globalMediumStatus === 'force_online') {
            if (status === 'typing' || status === 'reading') {
                return {
                    type: 'available' as const,
                    label: 'En train d\'écrire...',
                    shortLabel: 'Écrit...',
                    subLabel: 'Interlocuteur actif en direct',
                    badgeClass: 'bg-emerald-950/95 text-emerald-300 border-emerald-500/60 shadow-[0_0_12px_rgba(16,185,129,0.35)]',
                    dotClass: 'bg-emerald-400 animate-ping',
                    headerBg: 'bg-gradient-to-r from-emerald-950 via-indigo-950 to-purple-950',
                    bannerBorder: 'border-emerald-500/40'
                };
            }
            return {
                type: 'available' as const,
                label: 'Disponible',
                shortLabel: 'Disponible',
                subLabel: 'En direct • Réponse immédiate',
                badgeClass: 'bg-emerald-950/95 text-emerald-300 border-emerald-500/60 shadow-[0_0_12px_rgba(16,185,129,0.35)]',
                dotClass: 'bg-emerald-400 animate-pulse',
                headerBg: 'bg-gradient-to-r from-emerald-950 via-indigo-950 to-purple-950',
                bannerBorder: 'border-emerald-500/40'
            };
        }

        const medStatus = currentMedium.status || 'available';
        if (medStatus === 'busy') {
            return {
                type: 'busy' as const,
                label: 'Occupé(e)',
                shortLabel: 'Occupé',
                subLabel: 'En consultation privée',
                badgeClass: 'bg-amber-950/95 text-amber-300 border-amber-500/60 shadow-[0_0_12px_rgba(245,158,11,0.35)]',
                dotClass: 'bg-amber-400 animate-pulse',
                headerBg: 'bg-gradient-to-r from-amber-950 via-orange-950 to-purple-950',
                bannerBorder: 'border-amber-500/40'
            };
        }

        if (medStatus === 'offline') {
            return {
                type: 'offline' as const,
                label: 'Hors ligne',
                shortLabel: 'Hors ligne',
                subLabel: 'Absent(e) actuellement',
                badgeClass: 'bg-zinc-900/95 text-zinc-300 border-zinc-700/80 shadow-md',
                dotClass: 'bg-zinc-500',
                headerBg: 'bg-gradient-to-r from-zinc-900 via-gray-900 to-zinc-900',
                bannerBorder: 'border-zinc-700/60'
            };
        }

        if (status === 'typing' || status === 'reading') {
            return {
                type: 'available' as const,
                label: 'En train d\'écrire...',
                shortLabel: 'Écrit...',
                subLabel: 'Interlocuteur actif en direct',
                badgeClass: 'bg-emerald-950/95 text-emerald-300 border-emerald-500/60 shadow-[0_0_12px_rgba(16,185,129,0.35)]',
                dotClass: 'bg-emerald-400 animate-ping',
                headerBg: 'bg-gradient-to-r from-emerald-950 via-indigo-950 to-purple-950',
                bannerBorder: 'border-emerald-500/40'
            };
        }

        return {
            type: 'available' as const,
            label: 'Disponible',
            shortLabel: 'Disponible',
            subLabel: 'En direct • Réponse immédiate',
            badgeClass: 'bg-emerald-950/95 text-emerald-300 border-emerald-500/60 shadow-[0_0_12px_rgba(16,185,129,0.35)]',
            dotClass: 'bg-emerald-400 animate-pulse',
            headerBg: 'bg-gradient-to-r from-purple-900 to-indigo-900',
            bannerBorder: 'border-emerald-500/40'
        };
    };

    const interlocutorStatus = getInterlocutorStatus();

    if (isMinimized) {
        return (
            <div className="relative group animate-fade-in-up flex flex-col items-center z-[999999]">
                {/* Indicateur de statut en temps réel AU-DESSUS de la session */}
                <div className="mb-2 flex items-center justify-center">
                    <div className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold border backdrop-blur-md transition-all shadow-xl ${interlocutorStatus.badgeClass}`}>
                        <span className={`w-2 h-2 rounded-full shrink-0 ${interlocutorStatus.dotClass}`}></span>
                        <span className="tracking-tight whitespace-nowrap">{interlocutorStatus.shortLabel}</span>
                    </div>
                </div>

                <button 
                    onClick={() => setMinimized(currentMedium.id, false)}
                    className="w-16 h-16 rounded-full border-2 border-gold-400 p-0.5 shadow-2xl bg-black hover:scale-110 transition-transform overflow-visible relative"
                    title={`Ouvrir le chat avec ${currentMedium.name} (${interlocutorStatus.label})`}
                >
                    <img src={currentMedium.imageUrl} alt={currentMedium.name} onError={(e) => { (e.currentTarget as HTMLImageElement).src = 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&q=80&w=300'; }} className="w-full h-full rounded-full object-cover"/>
                    
                    {/* Pastille de statut sur l'avatar */}
                    <span className={`absolute bottom-0 right-0 w-3.5 h-3.5 rounded-full border-2 border-black ${
                        interlocutorStatus.type === 'available' ? 'bg-emerald-500 animate-pulse' :
                        interlocutorStatus.type === 'busy' ? 'bg-amber-500 animate-pulse' : 'bg-zinc-500'
                    }`}></span>

                    {/* Étiquette Prénom */}
                    <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 bg-purple-900 text-[9px] font-bold px-1.5 py-0.5 rounded-full border border-gold-400/50 text-white whitespace-nowrap shadow-lg">
                        {currentMedium.name.split(' ')[0]}
                    </div>

                    {unreadCount > 0 && (
                        <div className="absolute -top-1 -right-1 bg-red-600 text-white text-xs font-bold w-6 h-6 flex items-center justify-center rounded-full border-2 border-black animate-bounce z-10">
                            {unreadCount}
                        </div>
                    )}
                </button>
            </div>
        );
    }

    return (
        <div className={`flex flex-col overflow-hidden bg-[#0c0817] relative ${
            embedded 
                ? 'w-full h-full rounded-2xl sm:rounded-3xl border-0 shadow-none z-10' 
                : 'w-[calc(100vw-0.5rem)] sm:w-[calc(100vw-1rem)] md:w-[480px] h-[68dvh] sm:h-[75dvh] md:h-[620px] max-h-[88dvh] rounded-t-2xl z-50 shadow-2xl border border-purple-500/40 animate-fade-in-up'
        }`}>
            
            {/* --- CSS ANIMATION POUR LES ÉTOILES --- */}
            <style>{`
                @keyframes chat-twinkle {
                    0%, 100% { opacity: 0.2; transform: scale(0.8); }
                    50% { opacity: 0.8; transform: scale(1.2); box-shadow: 0 0 4px rgba(255,255,255,0.8); }
                }
            `}</style>

            {/* --- STARDUST BACKGROUND LAYER --- */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
                {stars.map(s => (
                    <div key={s.id}
                         className="absolute bg-white rounded-full"
                         style={{
                             left: `${s.left}%`,
                             top: `${s.top}%`,
                             width: `${s.size}px`,
                             height: `${s.size}px`,
                             animation: `chat-twinkle ${s.duration}s infinite ease-in-out ${s.delay}s`,
                             opacity: 0.3
                         }}
                    />
                ))}
            </div>

            {/* EN-TÊTE UNIQUE ET PARFAITEMENT ÉQUILIBRÉ DU SALON DE DISCUSSION */}
            <div className="bg-gradient-to-r from-[#1C1038] via-[#120A24] to-[#1C1038] px-3 py-2 sm:px-4 sm:py-2.5 flex items-center justify-between border-b border-purple-700/70 shrink-0 relative z-30 shadow-xl gap-2">
                
                {/* GAUCHE : PHOTO DU MÉDIUM + NOM COMPLET & STATUT (FLEX-1 MIN-W-0) */}
                <div className="flex items-center gap-2.5 min-w-0 flex-1">
                    {/* BOUTON RETOUR : UNIQUEMENT SI EMBEDDED */}
                    {embedded && (
                        <button
                            onClick={handleCloseAttempt}
                            className="p-1.5 rounded-xl bg-purple-900/80 hover:bg-purple-800 text-purple-200 hover:text-white text-xs font-black flex items-center justify-center cursor-pointer transition-all shadow-md active:scale-95 shrink-0 border border-purple-600/50"
                            title="Retour à la messagerie"
                        >
                            <span className="text-base font-black leading-none">←</span>
                        </button>
                    )}

                    {/* PHOTO DE PROFIL DU MÉDIUM AVEC STATUT */}
                    <div className="relative shrink-0">
                        <img 
                            src={currentMedium.imageUrl} 
                            alt={currentMedium.name} 
                            onError={(e) => { (e.currentTarget as HTMLImageElement).src = 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&q=80&w=300'; }} 
                            className="w-9 h-9 sm:w-10 sm:h-10 rounded-full border-2 border-gold-400 object-cover shadow-lg" 
                        />
                        <span className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-[#120A24] ${
                            interlocutorStatus.type === 'available' ? 'bg-emerald-500 animate-pulse' :
                            interlocutorStatus.type === 'busy' ? 'bg-amber-500' : 'bg-zinc-500'
                        }`}></span>
                    </div>

                    {/* NOM DU MÉDIUM ET STATUT (SANS AUCUN ÉCRASEMENT) */}
                    <div className="min-w-0 flex-1">
                        <h4 className="font-extrabold text-white text-xs sm:text-sm truncate flex items-center gap-1.5 leading-tight">
                            <span className="truncate">{currentMedium.name}</span>
                        </h4>
                        <div className="flex items-center gap-1 text-[10px] text-purple-300/90 truncate mt-0.5">
                            <span className="text-emerald-400 font-bold shrink-0">● En direct</span>
                            <span className="text-purple-400/40 shrink-0">•</span>
                            <span className="truncate text-gray-300">{currentMedium.specialties[0]}</span>
                        </div>
                    </div>
                </div>

                {/* DROITE : ACTIONS ÉPURÉES (SHRINK-0) */}
                <div className="flex items-center gap-1.5 shrink-0">
                    {/* SOLDE D'ASTRES (VISIBLE EN PLEIN ÉCRAN / MD) */}
                    <div className="hidden md:inline-flex items-center gap-1 bg-black/70 border border-gold-400/60 px-2 py-0.5 rounded-full text-gold-300 text-[10px] font-bold shadow-sm backdrop-blur-sm">
                        <SparklesIcon className="w-3 h-3 text-gold-400" />
                        <span>{client?.loyaltyPoints ?? 0}</span>
                    </div>

                    {/* BOUTON TERMINER COMPACT */}
                    {!isTerminated && (
                        <button 
                            onClick={(e) => { e.stopPropagation(); handleManualEndChat(); }} 
                            className="bg-gradient-to-r from-red-600 via-rose-600 to-red-600 hover:from-red-500 hover:to-rose-500 text-white font-black text-[10px] sm:text-xs px-2 py-1 sm:px-2.5 sm:py-1.5 rounded-xl border border-red-400/60 shadow-md shadow-red-950/60 transition-all flex items-center gap-1 active:scale-95 cursor-pointer"
                            title="Terminer la consultation et noter mon expérience"
                        >
                            <XIcon className="w-4 h-4 text-rose-400 inline" />
                            <span className="font-bold tracking-wide hidden xs:inline">Terminer</span>
                        </button>
                    )}

                    {/* SON */}
                    <button 
                        onClick={(e) => { e.stopPropagation(); toggleSound(); }} 
                        className="p-1 hover:bg-white/10 rounded-full text-white/80 hover:text-white transition-colors cursor-pointer"
                        title={soundEnabled ? "Désactiver les notifications sonores" : "Activer les notifications sonores"}
                    >
                        {soundEnabled ? <VolumeIcon className="w-3.5 h-3.5 text-gold-400" /> : <VolumeXIcon className="w-3.5 h-3.5 text-gray-400" />}
                    </button>

                    {/* RÉDUIRE & FERMER (FLOTTANT UNIQUEMENT) */}
                    {!embedded && (
                        <>
                            <button onClick={(e) => { e.stopPropagation(); setMinimized(currentMedium.id, true); }} className="p-1 hover:bg-white/10 rounded-full text-white cursor-pointer" title="Réduire"><MinusIcon className="w-3.5 h-3.5" /></button>
                            <button onClick={(e) => { e.stopPropagation(); handleCloseAttempt(); }} className="p-1 hover:bg-red-500/20 rounded-full text-white cursor-pointer" title="Fermer"><XIcon className="w-3.5 h-3.5" /></button>
                        </>
                    )}
                </div>
            </div>

            {isTerminated && (
                <div className="bg-red-950/95 border-b border-red-500/60 p-2.5 text-center text-red-200 text-xs font-bold flex items-center justify-center gap-2 relative z-20 shadow-lg">
                    <XIcon className="w-4 h-4 text-rose-400 inline" />
                    <span>Compte banni par l'administration. Envoi de messages désactivé.</span>
                </div>
            )}

            {showRating && (
                <div className="absolute inset-0 z-50 bg-black/95 backdrop-blur-xl flex flex-col items-center justify-center p-6 text-center animate-fade-in">
                    <button 
                        onClick={onClose} 
                        className="absolute top-4 right-4 text-gray-400 hover:text-white p-2 rounded-full bg-white/10 hover:bg-white/20 transition-all cursor-pointer z-50"
                        title="Fermer"
                    >
                        <XIcon className="w-5 h-5" />
                    </button>
                    <div className="w-16 h-16 rounded-full border-2 border-gold-500 mb-3 overflow-hidden shadow-[0_0_20px_rgba(234,179,8,0.3)]">
                        <img src={currentMedium.imageUrl} alt="Medium" onError={(e) => { (e.currentTarget as HTMLImageElement).src = 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&q=80&w=300'; }} className="w-full h-full object-cover" />
                    </div>
                    
                    {/* Points gagnés pour cette session */}
                    {earnedSessionPoints > 0 && (
                        <div className="mb-3 inline-flex items-center gap-1.5 bg-gold-500/20 border border-gold-500/50 px-3 py-1 rounded-full text-gold-300 text-xs font-bold animate-bounce">
                            +{earnedSessionPoints} Points Fidélité Gagnés !
                        </div>
                    )}

                    <h3 className="text-2xl font-serif font-bold text-transparent bg-clip-text bg-gradient-to-r from-gold-300 to-yellow-600 mb-1">
                        Votre Expérience
                    </h3>
                    <p className="text-gray-400 text-xs mb-4 max-w-[280px] leading-relaxed">
                        Comment s'est passée votre voyance avec <span className="text-white font-bold">{currentMedium.name}</span> ?
                        <span className="block text-gold-400/90 font-medium mt-1">
                            {client?.lastReviewBonusDate === new Date().toISOString().split('T')[0]
                                ? "Merci de donner votre ressenti sur cette consultation !"
                                : "Évaluez la consultation pour gagner +5 Astres (1x par jour) !"}
                        </span>
                    </p>
                    <div className="flex gap-3 mb-4">
                        {[1, 2, 3, 4, 5].map((star) => (
                            <button 
                                key={star} 
                                onClick={() => setUserRating(star)}
                                className="transition-transform hover:scale-125 focus:outline-none"
                            >
                                <StarIcon className={`w-8 h-8 ${star <= userRating ? 'text-gold-400 fill-current drop-shadow-[0_0_10px_rgba(251,191,36,0.5)]' : 'text-gray-800'}`} />
                            </button>
                        ))}
                    </div>
                    <textarea
                        value={reviewText}
                        onChange={(e) => setReviewText(e.target.value)}
                        placeholder="Un petit commentaire sur vos prédictions ? (Optionnel)"
                        className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-sm text-white mb-4 focus:border-gold-500/50 outline-none resize-none h-20 placeholder-gray-600"
                    />
                    <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 w-full">
                        <button onClick={onClose} className="w-full sm:w-auto sm:flex-initial py-2.5 sm:py-3 px-4 rounded-xl border border-white/10 text-gray-400 hover:text-white hover:bg-white/5 text-xs sm:text-sm font-bold transition-colors">
                            Passer
                        </button>
                        <button 
                            onClick={handleSubmitRating} 
                            disabled={userRating === 0} 
                            className="w-full sm:flex-1 py-2.5 sm:py-3 px-3 rounded-xl bg-gold-600 hover:bg-gold-500 text-black font-extrabold text-xs sm:text-sm shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center text-center leading-normal"
                        >
                            {client?.lastReviewBonusDate === new Date().toISOString().split('T')[0]
                                ? "Envoyer l'évaluation"
                                : "Envoyer & Gagner +5 Astres"}
                        </button>
                    </div>
                </div>
            )}

            {showPhoneInvite && !showRating && !isLimitReached && !isTerminated && (
                <div className="absolute inset-x-0 bottom-0 z-40 p-4 animate-slide-up bg-gradient-to-t from-black via-mystic-900 to-transparent pt-12 pointer-events-none">
                    <div className="pointer-events-auto bg-gradient-to-br from-indigo-900 to-black border border-gold-500/50 rounded-xl p-5 shadow-[0_-5px_30px_rgba(234,179,8,0.2)] relative overflow-hidden">
                        <div className="absolute inset-0 opacity-20 pointer-events-none flex items-center justify-center gap-1">
                            {[...Array(10)].map((_, i) => (
                                <div key={i} className="w-2 bg-gold-500 rounded-full animate-bounce" style={{ height: `${Math.random() * 100}%`, animationDelay: `${i * 0.1}s`, animationDuration: '1s' }}></div>
                            ))}
                        </div>
                        <button onClick={() => setShowPhoneInvite(false)} className="absolute top-2 right-2 text-gray-400 hover:text-white bg-black/40 rounded-full p-1 transition-colors z-20"><XIcon className="w-4 h-4" /></button>
                        <div className="relative z-10 text-center">
                            <div className="inline-flex items-center gap-2 bg-gold-500/20 border border-gold-500/50 rounded-full px-3 py-1 mb-3">
                                <span className="relative flex h-2 w-2">
                                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-gold-400 opacity-75"></span>
                                  <span className="relative inline-flex rounded-full h-2 w-2 bg-gold-500"></span>
                                </span>
                                <span className="text-[10px] font-bold text-gold-300 uppercase tracking-widest">Flash Médiumnique</span>
                            </div>
                            <h3 className="text-xl font-serif font-bold text-white mb-2">J'ai un ressenti intense...</h3>
                            <p className="text-sm text-gray-300 mb-4 leading-relaxed">Je capte une information cruciale pour votre avenir, mais j'ai absolument besoin de la <strong className="text-gold-400">vibration de votre voix</strong> pour la confirmer avec précision. C'est le moment.</p>
                            <a href="tel:0985405274" className="block w-full py-3 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white font-bold rounded-lg shadow-lg shadow-green-900/50 transition-transform hover:scale-105 flex items-center justify-center gap-2 animate-pulse">
                                <PhoneIcon className="w-5 h-5" /> Appeler maintenant (Sans attente)
                            </a>
                            <p className="text-[10px] text-gray-500 mt-2">Consultation privée immédiate • Discrétion totale</p>
                        </div>
                    </div>
                </div>
            )}



            <div ref={messageContainerRef} onScroll={handleMessagesScroll} className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3 bg-black/40 scroll-smooth relative z-10 custom-scrollbar">
                {status === 'countdown' && countdown !== null && !isTerminated && (
                    <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-black/60 backdrop-blur-sm">
                        <div className="text-4xl font-bold text-white mb-2 font-mono">{countdown}</div>
                        <div className="text-purple-300 uppercase text-[10px] tracking-widest animate-pulse">Connexion...</div>
                    </div>
                )}

                {messages.map((msg) => {
                    const isUser = msg.role === 'user';
                    return (
                        <div key={msg.id} className={`flex ${isUser ? 'justify-end' : 'justify-start'} animate-fade-in-up`}>
                            <div className={`max-w-[85%] px-4 py-2.5 pb-5 rounded-2xl text-sm shadow-md leading-relaxed relative ${isUser ? 'bg-indigo-600 text-white rounded-br-none' : 'bg-mystic-800/90 text-gray-100 rounded-bl-none border border-white/10'}`}>
                                <div className="whitespace-pre-line leading-relaxed font-normal select-text">
                                    {msg.text.includes('🃏 **Tirage de Cartes') ? (
                                        <div className="space-y-2">
                                            <div className="font-black text-amber-300 flex items-center gap-1.5 text-xs uppercase tracking-wider pb-1 border-b border-amber-500/20">
                                                <CardsIcon className="w-4 h-4 text-amber-400 inline mr-1" /> Tirage des Arcanes & Guidance
                                            </div>
                                            <div className="text-gray-100 font-medium">{msg.text}</div>
                                        </div>
                                    ) : (
                                        msg.text
                                    )}
                                </div>
                                {msg.imageUrl && (
                                    <div className="mt-2">
                                        <img 
                                            src={formatImageUrl(msg.imageUrl)} 
                                            alt="Pièce jointe" 
                                            className="max-w-full rounded-xl max-h-64 object-contain border border-white/20 shadow-md select-none pointer-events-auto" 
                                        />
                                    </div>
                                )}
                                {isUser && (
                                    <div className="absolute bottom-1 right-3 flex items-center gap-0.5 text-[10px] text-sky-200 font-medium select-none" title="Message lu / délivré">
                                        <CheckCheckIcon className="w-3.5 h-3.5 text-sky-300 drop-shadow-sm inline-block" />
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
                
                {(status === 'reading') && (
                    <div className="flex justify-start animate-pulse">
                        <div className="text-[10px] text-gray-500 italic flex items-center gap-2 px-2">
                            <EyeIcon className="w-3 h-3" /> Vu
                        </div>
                    </div>
                )}

                {(status === 'typing') && (
                    <div className="flex justify-start animate-fade-in">
                        <div className="bg-mystic-800/50 border border-white/5 px-4 py-3 rounded-2xl rounded-bl-none flex items-center gap-1 w-fit">
                            <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"></div>
                            <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce delay-100"></div>
                            <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce delay-200"></div>
                        </div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* OVERLAY FERMETURE / TERMINÉ */}
            {isTerminated && (
                <div className="absolute inset-x-0 bottom-0 top-[57px] z-40 bg-mystic-950/95 backdrop-blur-md flex flex-col items-center justify-center p-6 text-center animate-fade-in border-t border-red-500/30">
                    <div className="w-12 h-12 rounded-full bg-red-900/40 border border-red-500/50 flex items-center justify-center mb-3">
                        <LockIcon className="w-6 h-6 text-red-400" />
                    </div>
                    <h3 className="text-lg font-serif font-bold text-white mb-1">Discussion Clôturée</h3>
                    <p className="text-red-200/80 text-xs mb-5 max-w-xs leading-relaxed">
                        Le médium a mis fin à cet échange conformément à notre charte déontologique.
                    </p>
                    <button onClick={onClose} className="bg-red-800 hover:bg-red-700 text-white font-bold py-2.5 px-6 rounded-xl text-xs transition-colors shadow-lg">
                        Fermer le chat
                    </button>
                </div>
            )}

            {/* OVERLAY LIMITE GRATUITE ATTEINTE (BANNIÈRE DE CONTINUATION PRO) */}
            {isLimitReached && !isTerminated && (
                <div className="absolute inset-x-0 bottom-0 top-[57px] z-40 bg-black/90 backdrop-blur-md flex flex-col items-center justify-center p-6 text-center animate-fade-in border-t border-gold-500/30">
                    <div className="relative mb-3">
                        <img 
                            src={currentMedium.imageUrl} 
                            alt={currentMedium.name} 
                            onError={(e) => { (e.currentTarget as HTMLImageElement).src = 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&q=80&w=300'; }} 
                            className="w-16 h-16 rounded-full border-2 border-gold-400 object-cover shadow-[0_0_20px_rgba(234,179,8,0.4)]"
                        />
                        <div className="absolute -bottom-1 -right-1 bg-gold-500 text-black p-1.5 rounded-full shadow-lg border border-black">
                            <LockIcon className="w-3.5 h-3.5" />
                        </div>
                    </div>

                    <div className="inline-flex items-center gap-1.5 bg-gold-500/10 border border-gold-500/30 text-gold-300 text-[10px] font-semibold px-3 py-1 rounded-full uppercase tracking-wider mb-2">
                        <span className="w-2 h-2 rounded-full bg-gold-400 animate-pulse"></span>
                        Limite découverte atteinte
                    </div>

                    <h3 className="text-lg md:text-xl font-serif font-bold text-white mb-1 leading-tight">
                        Inscrivez-vous pour continuer
                    </h3>

                    <p className="text-gray-300 text-xs mb-4 max-w-xs leading-relaxed">
                        Pour sauvegarder votre historique et poursuivre cet échange en direct avec <span className="text-gold-300 font-bold">{currentMedium.name}</span>, créez votre compte gratuit.
                    </p>

                    <div className="w-full max-w-xs space-y-1.5 mb-5 text-left bg-white/5 border border-white/10 rounded-xl p-3 text-[11px] text-gray-300">
                        <div className="flex items-center gap-2">
                            <CheckIcon className="w-3.5 h-3.5 text-green-400 inline" />
                            <span>Inscription 100% gratuite et instantanée</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <CheckIcon className="w-3.5 h-3.5 text-green-400 inline" />
                            <span>Sauvegarde de vos échanges et thèmes</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <CheckIcon className="w-3.5 h-3.5 text-green-400 inline" />
                            <span>Accès illimité aux médiums certifiés</span>
                        </div>
                    </div>

                    <button 
                        onClick={handleForceRegister}
                        className="w-full max-w-xs bg-gradient-to-r from-gold-500 via-yellow-400 to-gold-600 hover:from-gold-400 hover:to-yellow-300 text-black font-extrabold py-3 px-5 rounded-xl shadow-[0_0_20px_rgba(234,179,8,0.35)] transition-all hover:scale-[1.02] active:scale-95 text-xs uppercase tracking-wider"
                    >
                        Créer mon compte gratuit
                    </button>

                    <p className="text-[10px] text-gray-500 mt-2.5">Sans engagement • Activation en 1 clic</p>
                </div>
            )}

            {/* BOUTON FLOTTANT NOUVEAU MESSAGE (QUAND LE CLIENT A SCROLLÉ VERS LE HAUT) */}
            {showNewMessageBadge && (
                <div className="absolute bottom-[72px] sm:bottom-[80px] left-1/2 -translate-x-1/2 z-30 pointer-events-auto">
                    <button
                        onClick={scrollToBottom}
                        className="bg-gradient-to-r from-gold-500 via-amber-400 to-yellow-400 text-black font-extrabold text-xs px-4 py-2 rounded-full shadow-[0_4px_20px_rgba(245,158,11,0.6)] border border-amber-200 flex items-center gap-2 animate-bounce hover:scale-105 active:scale-95 transition-all cursor-pointer select-none"
                    >
                        <span>Nouveau message</span>
                        <span className="text-sm font-black">↓</span>
                    </button>
                </div>
            )}

            <div className="p-2.5 sm:p-3 bg-mystic-900 border-t border-white/10 shrink-0 relative z-10">
                {uploadError && (
                    <div className="mb-2 px-3 py-1.5 bg-red-950/90 border border-red-500/50 rounded-xl text-red-200 text-xs font-medium flex items-center justify-between animate-fade-in">
                        <span>{uploadError}</span>
                        <button onClick={() => setUploadError(null)} className="text-red-400 hover:text-white font-bold ml-2">×</button>
                    </div>
                )}
                <div className="flex gap-2 items-end">
                    <label 
                        className={`h-11 w-11 sm:h-12 sm:w-12 flex items-center justify-center bg-white/10 hover:bg-white/20 text-gray-200 rounded-full cursor-pointer shadow-md shrink-0 transition-transform active:scale-95 ${isLimitReached || isTerminated ? 'opacity-50 pointer-events-none' : ''}`}
                        title="Envoyer une image connue (.jpg, .jpeg, .png, .webp, .gif, .bmp, .svg)"
                    >
                        <CameraIcon className="w-4 h-4 text-purple-300 inline" />
                        <input 
                            type="file" 
                            accept=".jpg,.jpeg,.png,.webp,.gif,.bmp,.svg,image/jpeg,image/png,image/webp,image/gif,image/bmp,image/svg+xml" 
                            disabled={isLimitReached || isTerminated}
                            onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file || !sessionId) return;

                                const validExtensions = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'svg'];
                                const fileExt = file.name.split('.').pop()?.toLowerCase();
                                const isKnownImage = fileExt && validExtensions.includes(fileExt);

                                if (!isKnownImage) {
                                    setUploadError("Format d'image non supporté. Veuillez envoyer une image valide (.jpg, .jpeg, .png, .webp, .gif, .bmp, .svg).");
                                    e.target.value = '';
                                    return;
                                }

                                setUploadError(null);
                                try {
                                    // 1. Compresser l'image côté client (canvas) pour réduire la taille (ex: de 5MB à ~150KB)
                                    const base64 = await compressImage(file, 1200, 1200, 0.8);
                                    let finalImageUrl = base64; // Fallback direct si serveur d'upload indisponible

                                    // 2. Tenter de poster l'image sur le serveur VPS
                                    try {
                                        const apiUrl = `${getApiUrl()}/api/upload`;
                                        const res = await fetch(apiUrl, {
                                            method: 'POST',
                                            headers: { 'Content-Type': 'application/json' },
                                            body: JSON.stringify({ image: base64, name: `chat_${Date.now()}` })
                                        });

                                        if (res.ok) {
                                            const data = await res.json();
                                            if (data && data.url) {
                                                finalImageUrl = formatImageUrl(data.url);
                                            }
                                        }
                                    } catch (uploadErr) {
                                        console.warn("API Upload VPS non disponible, utilisation de la Data URL locale:", uploadErr);
                                    }

                                    // 3. Créer et diffuser le message avec l'image
                                    const userMsg: ChatMessage = {
                                        id: Date.now().toString(),
                                        role: 'user',
                                        text: '[Image partagée au médium]',
                                        imageUrl: finalImageUrl,
                                        timestamp: new Date()
                                    };
                                    addMessageToSession(sessionId, userMsg);
                                } catch (err) {
                                    console.error("Erreur globale upload image chat", err);
                                    setUploadError("Erreur lors de la préparation de l'image.");
                                } finally {
                                    e.target.value = '';
                                }
                            }}
                            className="hidden" 
                        />
                    </label>

                    <textarea 
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyPress={(e) => { if(e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                        placeholder="Message..."
                        disabled={isLimitReached || isTerminated}
                        enterKeyHint="send"
                        className="flex-1 bg-black/60 border border-white/15 rounded-xl px-3.5 py-2.5 text-white focus:outline-none focus:border-purple-500 text-xs sm:text-sm resize-none h-11 sm:h-12 leading-relaxed disabled:opacity-50"
                    />
                    <button 
                        onClick={handleSend} 
                        disabled={isLimitReached || isTerminated}
                        className="h-11 w-11 sm:h-12 sm:w-12 flex items-center justify-center bg-gold-500 rounded-full hover:bg-gold-400 text-black shadow-lg disabled:opacity-50 shrink-0 active:scale-95 transition-transform"
                    >
                        <SendIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default LiveChat;